import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { Permission } from '__generated__/graphql';
import { gql } from '__generated__';

import { EditAppPermissionsForm } from './EditPermissionsForm';
import { getServerErrors } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import GetAppsForProjectQuery from './GetAppsForProjectQuery';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useValidation from '../../common/useValidation';

const editAppPermissionsSchema = yup.object().shape({
  permissions: yup.array().min(1).required(),
});

export const EditAppPermissionsMutation = gql(`
  mutation EditAppPermissions($input: EditAppPermissionsInput!) {
    editAppPermissions(input: $input) {
      id
    }
  }
`);

interface App {
  id: string;
  name: string;
}

export interface EditAppPermissionsModalProps {
  app: App;
  permissions: Permission[];
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const EditAppPermissionsModal = ({
  app, permissions, onSuccess, onCancel,
  refetchQueries = [GetAppsForProjectQuery],
}: EditAppPermissionsModalProps,
) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const form = useForm({
    values: {
      permissions,
    },
  });

  const [editApp, { loading, error }] = useMutation(
    EditAppPermissionsMutation, {
      variables: {
        input: {
          project_id: project.id,
          app_id: app.id,
          ...form.values,
        },
      },
      onCompleted: () => {
        notifier.success(t('notifications.app_saved'));
        onSuccess?.();
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const { valid, errors } = useValidation({
    schema: editAppPermissionsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editApp()}>
      <UI.FormGrid>
        <UI.H4>
          {`${t('edit_app')}: ${app.name}`}
        </UI.H4>

        <UI.HR />

        <EditAppPermissionsForm form={form} errors={errors} />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseEditAppPermissionsModalProps {
  app: App;
  permissions: Permission[];
}

const useEditAppPermissionsModal = ({ app, permissions }: UseEditAppPermissionsModalProps) => {
  const [openModal, closeModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeModal}>
      <EditAppPermissionsModal
        app={app}
        permissions={permissions}
        onSuccess={closeModal}
        onCancel={closeModal}
      />
    </UI.Modal>
  ), [app, permissions]);

  return [openModal, closeModal];
};

export default useEditAppPermissionsModal;
